import React, { useEffect, useState } from 'react';
import { HelpCircle, LifeBuoy, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import FormPageShell from '../components/Common/FormPageShell';
import PageHeader from '../components/Common/PageHeader';
import FormSection from '../components/Common/FormSection';
import ComplaintPhotoSlider from '../components/Complaint/ComplaintPhotoSlider';
import { getAppHomePath } from '../constants/navigation';

const FAQ = [
  { q: 'O chamado é sobre o sistema ou sobre um processo?', a: 'Use este canal para falha ou dúvida do useAmbiental. Denúncia ambiental continua em Denúncias.' },
  { q: 'Quem recebe o e-mail?', a: 'Você e a fila da Mangue. Se marcar a cópia, o admin do órgão também recebe.' },
  { q: 'Qual o prazo da primeira resposta?', a: 'Oito horas em dias úteis, das 9h às 18h.' },
];

const STATUS_LABEL = {
  open: 'Aberto',
  in_progress: 'Em andamento',
  waiting_customer: 'Aguardando você',
  resolved: 'Resolvido',
};

const STATUS_CLASS = {
  open: 'bg-primary-50 text-primary-800',
  in_progress: 'bg-amber-50 text-amber-800',
  waiting_customer: 'bg-sky-50 text-sky-800',
  resolved: 'bg-emerald-50 text-emerald-800',
};

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const EMPTY_FORM = {
  subject: '',
  body: '',
  process_ref: '',
  notify_tenant_admin: false,
  tenant_admin_email: '',
};

function errText(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return error?.message || 'Não foi possível falar com o suporte.';
}

function formatWhen(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function acceptImages(files) {
  const accepted = [];
  files.forEach((file) => {
    if (!IMAGE_TYPES.has(file.type)) {
      toast.error(`${file.name} precisa ser JPG, PNG, WEBP ou GIF.`);
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(`${file.name} passa de 20 MB.`);
      return;
    }
    accepted.push(file);
  });
  return accepted;
}

async function uploadImages(ticketId, files) {
  for (const file of files) {
    const body = new FormData();
    body.append('file', file);
    await api.post(`/api/v1/support/tickets/${ticketId}/attachments`, body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

function StatusPill({ status }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[status] || 'bg-gray-100 text-gray-700'}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [tenantWide, setTenantWide] = useState(false);
  const [viewerUserId, setViewerUserId] = useState('');
  const [status, setStatus] = useState(null);
  const [openId, setOpenId] = useState('');
  const [thread, setThread] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [reply, setReply] = useState('');
  const [pendingItems, setPendingItems] = useState([]);

  async function load() {
    const [{ data: list }, { data: platform }] = await Promise.all([
      api.get('/api/v1/support/tickets'),
      api.get('/api/v1/support/status'),
    ]);
    if (Array.isArray(list)) {
      setTickets(list);
      setTenantWide(false);
      setViewerUserId('');
    } else {
      setTickets(Array.isArray(list?.tickets) ? list.tickets : []);
      setTenantWide(Boolean(list?.tenant_wide));
      setViewerUserId(String(list?.viewer_user_id || ''));
    }
    setStatus(platform);
  }

  useEffect(() => {
    load()
      .catch((loadError) => setError(errText(loadError)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const next = images.map((file, index) => ({
      id: `pending-${index}-${file.name}-${file.size}`,
      src: URL.createObjectURL(file),
      file,
      alt: file.name,
    }));
    setPendingItems(next);
    return () => {
      next.forEach((item) => URL.revokeObjectURL(item.src));
    };
  }, [images]);

  async function open(id) {
    const { data } = await api.get(`/api/v1/support/tickets/${id}`);
    setOpenId(id);
    setThread(data);
    return data;
  }

  async function create(event) {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      const { data } = await api.post('/api/v1/support/tickets', {
        subject: form.subject,
        body: form.body,
        screen_url: window.location.pathname,
        context: { license_or_process: form.process_ref },
        notify_tenant_admin: form.notify_tenant_admin,
        tenant_admin_email: form.tenant_admin_email,
      });
      const pending = images;
      setForm(EMPTY_FORM);
      setImages([]);
      if (pending.length) {
        setUploading(true);
        await uploadImages(data.id, pending);
      }
      await open(data.id);
      await load();
      toast.success('Chamado aberto.');
    } catch (createError) {
      const message = errText(createError);
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
      setUploading(false);
    }
  }

  async function sendReply(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/api/v1/support/tickets/${openId}/messages`, { body: reply });
      setReply('');
      setThread(data);
      await load();
    } catch (replyError) {
      toast.error(errText(replyError));
    } finally {
      setSaving(false);
    }
  }

  async function addImages(files) {
    const accepted = acceptImages(files);
    if (!accepted.length) return;
    if (!openId) {
      setImages((current) => [...current, ...accepted]);
      return;
    }
    setUploading(true);
    try {
      await uploadImages(openId, accepted);
      await open(openId);
      toast.success(accepted.length === 1 ? 'Imagem enviada.' : 'Imagens enviadas.');
    } catch (uploadError) {
      toast.error(errText(uploadError));
    } finally {
      setUploading(false);
    }
  }

  const savedImages = (thread?.attachments || [])
    .filter((file) => String(file.content_type || '').startsWith('image/'))
    .map((file) => ({
      id: file.id,
      path: `/api/v1/support/tickets/${thread.id}/attachments/${file.id}`,
      alt: file.filename,
    }));

  const myTickets = viewerUserId
    ? tickets.filter((ticket) => ticket.author_user_id === viewerUserId)
    : tickets;
  const orgTickets = tenantWide && viewerUserId
    ? tickets.filter((ticket) => ticket.author_user_id !== viewerUserId)
    : [];
  const openIncident = (status?.open_incidents || []).find((item) => item.status !== 'resolved')
    || (status?.status && status.status !== 'operational' ? (status.incidents || [])[0] : null);
  const operational = !openIncident && (status?.status === 'operational' || !status?.status);

  function ticketButton(ticket, { showAuthor = false } = {}) {
    return (
      <button
        type="button"
        className={`w-full px-4 py-3 text-left hover:bg-gray-50 ${openId === ticket.id ? 'bg-primary-50/60' : ''}`}
        onClick={() => open(ticket.id).catch((openError) => toast.error(errText(openError)))}
      >
        <span className="flex items-start justify-between gap-2">
          <span className="font-medium text-gray-900">{ticket.subject}</span>
          <StatusPill status={ticket.status} />
        </span>
        <span className="mt-1 block text-xs text-gray-500">
          {showAuthor ? `${ticket.author_name || ticket.author_email || 'Colega'} · ` : ''}
          {formatWhen(ticket.updated_at || ticket.created_at)}
          {ticket.sla_breached ? ' · prazo da primeira resposta vencido' : ''}
        </span>
      </button>
    );
  }

  return (
    <FormPageShell maxWidth="max-w-6xl">
      <PageHeader
        title="Falar com o suporte"
        description="Dúvida ou falha do useAmbiental. Órgão e requerente usam o mesmo canal."
        breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Suporte' }]}
      >
        {status && (
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${operational ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
            {operational ? 'Operação normal' : 'Incidente em andamento'}
          </span>
        )}
      </PageHeader>

      {openIncident && (
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
          <strong>{openIncident.title}</strong>
          {openIncident.body ? ` — ${openIncident.body}` : ''}
        </p>
      )}

      {error && (
        <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
      )}

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6">
          <FormSection
            id="support-new"
            title="Novo chamado"
            description="Descreva o que aconteceu. As imagens ajudam a equipe a ver a tela."
            icon={LifeBuoy}
          >
            <form onSubmit={create} className="form-stack">
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="support-subject">Assunto</label>
                <input
                  id="support-subject"
                  className="form-input"
                  value={form.subject}
                  onChange={(event) => setForm({ ...form, subject: event.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="support-body">O que aconteceu</label>
                <textarea
                  id="support-body"
                  className="form-textarea min-h-[120px]"
                  value={form.body}
                  onChange={(event) => setForm({ ...form, body: event.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="support-ref">Licença ou processo</label>
                <input
                  id="support-ref"
                  className="form-input"
                  placeholder="Opcional"
                  value={form.process_ref}
                  onChange={(event) => setForm({ ...form, process_ref: event.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-primary-500"
                  checked={form.notify_tenant_admin}
                  onChange={(event) => setForm({ ...form, notify_tenant_admin: event.target.checked })}
                />
                Copiar o admin do órgão
              </label>
              {form.notify_tenant_admin && (
                <div className="form-group">
                  <label className="form-label form-label-required" htmlFor="support-admin-email">E-mail do admin do órgão</label>
                  <input
                    id="support-admin-email"
                    className="form-input"
                    type="email"
                    value={form.tenant_admin_email}
                    onChange={(event) => setForm({ ...form, tenant_admin_email: event.target.value })}
                    required
                  />
                </div>
              )}
              <ComplaintPhotoSlider
                title="Imagens"
                emptyLabel="Nenhuma imagem anexada."
                addLabel="Adicionar imagem"
                items={pendingItems}
                adding={uploading && !openId}
                onAdd={(files) => setImages((current) => [...current, ...acceptImages(files)])}
                onRemove={(item) => setImages((current) => current.filter((file) => file !== item.file))}
              />
              <p className="-mt-2 text-xs text-gray-500">JPG, PNG, WEBP ou GIF, até 20 MB cada.</p>
              <div>
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? 'Abrindo…' : 'Abrir chamado'}
                </button>
              </div>
            </form>
          </FormSection>

          {thread && (
            <FormSection
              id="support-thread"
              title={thread.subject}
              description={STATUS_LABEL[thread.status] || thread.status}
              icon={MessageSquare}
              variant="slate"
            >
              <div className="space-y-3">
                {(thread.messages || []).map((message) => {
                  const fromOperator = message.author_kind === 'operator';
                  return (
                    <article
                      key={message.id}
                      className={`rounded-lg px-3 py-2 ${fromOperator ? 'bg-primary-50' : 'bg-gray-50'}`}
                    >
                      <p className="text-xs font-semibold text-gray-500">
                        {fromOperator ? 'Suporte Mangue' : (message.author_email === thread.author_email ? 'Autor' : 'Organização')}
                        {message.created_at ? ` · ${formatWhen(message.created_at)}` : ''}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{message.body}</p>
                    </article>
                  );
                })}
              </div>
              <div className="mt-5">
                <ComplaintPhotoSlider
                  title="Imagens do chamado"
                  emptyLabel="Nenhuma imagem neste chamado."
                  addLabel="Enviar imagem"
                  items={savedImages}
                  adding={uploading}
                  onAdd={addImages}
                />
              </div>
              <form onSubmit={sendReply} className="mt-5 space-y-3">
                <label className="form-label" htmlFor="support-reply">Resposta</label>
                <textarea
                  id="support-reply"
                  className="form-textarea min-h-[96px]"
                  value={reply}
                  onChange={(event) => setReply(event.target.value)}
                  required
                />
                <button className="btn btn-outline" type="submit" disabled={saving}>Responder</button>
              </form>
            </FormSection>
          )}
        </div>

        <div className="space-y-6">
          <section className="data-list-card">
            <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
              <LifeBuoy className="h-5 w-5 text-primary-600" />
              <h2 className="text-base font-semibold text-gray-900">Meus chamados</h2>
            </div>
            {loading && <p className="p-4 text-sm text-gray-600">Carregando…</p>}
            {!loading && myTickets.length === 0 && (
              <p className="p-4 text-sm text-gray-600">Nenhum chamado aberto por você.</p>
            )}
            <ul className="divide-y divide-gray-100">
              {myTickets.map((ticket) => (
                <li key={ticket.id}>{ticketButton(ticket)}</li>
              ))}
            </ul>
          </section>

          {tenantWide && (
            <section className="data-list-card">
              <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                <LifeBuoy className="h-5 w-5 text-primary-600" />
                <h2 className="text-base font-semibold text-gray-900">Chamados da organização</h2>
              </div>
              {!loading && orgTickets.length === 0 && (
                <p className="p-4 text-sm text-gray-600">Nenhum chamado de outros usuários do órgão.</p>
              )}
              <ul className="divide-y divide-gray-100">
                {orgTickets.map((ticket) => (
                  <li key={ticket.id}>{ticketButton(ticket, { showAuthor: true })}</li>
                ))}
              </ul>
            </section>
          )}

          <FormSection
            id="support-faq"
            title="Perguntas frequentes"
            icon={HelpCircle}
            variant="emerald"
            bodyClassName="divide-y divide-gray-100 p-0"
          >
            {FAQ.map((item) => (
              <details key={item.q} className="group px-5 py-3">
                <summary className="cursor-pointer text-sm font-medium text-gray-900">{item.q}</summary>
                <p className="mt-2 text-sm text-gray-600">{item.a}</p>
              </details>
            ))}
          </FormSection>
        </div>
      </div>
    </FormPageShell>
  );
}
